import { Model, modelRegistry } from './Model';
import { TaxRate } from '../models';
import { I18n } from '../wrappers';
import Money from '../utilities/Money';
export class TaxItem extends Model {
    static entity = 'tax_items';
    id = null;
    label = '';
    rate = '0.00';
    type = null;
    name = null;
    num_of_items = 0;
    price_before_tax = '0.0000';
    value = '0.0000';
    additional_discount_amount = '0.0000';
    additional_count_amount = '0.0000';
    tax_group_revision = 0;
    created_at = null;
    updated_at = null;
    static fields() {
        return {
            id: this.attr(null),
            label: this.attr(''),
            rate: this.attr('0.00', value => this.round(value, 2).toFixed(2)),
            type: this.attr(null),
            name: this.attr(null),
            num_of_items: this.attr(0),
            price_before_tax: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            value: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            additional_discount_amount: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            additional_count_amount: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            tax_group_revision: this.attr(null),
            created_at: this.attr(null),
            updated_at: this.attr(null),
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    /**
     * Make a duplicate of the current tax item.
     */
    $duplicate() {
        return new TaxItem(this.$toJson());
    }
    getNumOfItems() {
        return this.num_of_items;
    }
    getPriceBeforeTax() {
        return Money.parse(this.price_before_tax);
    }
    getRate() {
        return Money.parse(this.rate);
    }
    getValue() {
        return Money.parse(this.value);
    }
    getAdditionalDiscountAmount() {
        return Money.parse(this.additional_discount_amount);
    }
    getAdditionalCountAmount() {
        return Money.parse(this.additional_count_amount);
    }
    sameAs(taxItem) {
        return (!!this.id && this.id === taxItem.id) || this.label === taxItem.label;
    }
    static createForOrderItem(order, orderItem) {
        const taxRate = TaxRate.find(orderItem.tax_rate_id);
        if (!taxRate)
            return null;
        const priceBeforeTax = !order.isCreditNote()
            ? orderItem.getPriceBeforeTax()
            : orderItem.getAdditionalPriceBeforeTax();
        const tax = !order.isCreditNote()
            ? orderItem.getTax()
            : orderItem.getAdditionalTax();
        const additionalDiscountAmount = orderItem.getAdditionalDiscountPercent() != 0
            ? orderItem.getAdditionalPriceBeforeTax()
            : Money.parse(0);
        const additionalCountAmount = orderItem.getAdditionalCount() != 0
            ? orderItem.getAdditionalPriceBeforeTax()
            : Money.parse(0);
        return new TaxItem({
            id: I18n.c('order.exemptFromTaxSetsTaxRate') || !orderItem.exempt_from_tax ? taxRate.id : null,
            label: (I18n.c('order.exemptFromTaxSetsTaxRate') || !orderItem.exempt_from_tax ? taxRate.label : orderItem.exempt_from_tax) || '',
            rate: I18n.c('order.exemptFromTaxSetsTaxRate') || !orderItem.exempt_from_tax ? taxRate.rate : '0.00',
            type: taxRate.type,
            name: taxRate.name,
            num_of_items: 1,
            price_before_tax: priceBeforeTax.toFormat('0.0000'),
            value: tax.toFormat('0.0000'),
            additional_discount_amount: additionalDiscountAmount.toFormat('0.0000'),
            additional_count_amount: additionalCountAmount.toFormat('0.0000'),
            tax_group_revision: 1
        });
    }
    static createForTaxRateOnTotal(order, taxRate) {
        const value = order.getToPay().percentage(taxRate.getRate()).toFormat('0.0000');
        return new TaxItem({
            id: taxRate.id,
            label: taxRate.label || '',
            rate: taxRate.rate,
            type: taxRate.type,
            name: taxRate.name,
            num_of_items: 0,
            price_before_tax: '0.0000',
            value: Money.parse(value).toFormat('0.0000'),
            additional_discount_amount: '0.0000',
            additional_count_amount: '0.0000',
            tax_group_revision: 1
        });
    }
    static createForSrFiscalTaxItem(order, taxItem, invoice, taxGroupRevision) {
        const isRefund = invoice.transactionType === 'Refund';
        const taxRate = TaxRate.all().find(t => t.label === taxItem.label);
        if (!taxRate) {
            return null;
        }
        const priceBeforeTax = order.items
            .filter(item => item.tax_rate_id == taxRate.id)
            .reduce((acc, cur) => acc.add(cur.getPriceBeforeTax()), Money.parse(0));
        return new TaxItem({
            id: taxRate.id,
            label: taxItem.label,
            rate: Money.parse(taxItem.rate).toFormat('0.00'),
            type: taxRate.type,
            name: taxRate.name,
            num_of_items: invoice.items.filter(item => taxItem.label && item.labels.includes(taxItem.label)).length,
            price_before_tax: priceBeforeTax.toFormat('0.0000'),
            value: Money.parse(isRefund ? -1 * taxItem.amount : taxItem.amount).toFormat('0.0000'),
            additional_discount_amount: '0.0000',
            additional_count_amount: '0.0000',
            tax_group_revision: taxGroupRevision
        });
    }
    mergeWith(taxItem) {
        this.num_of_items += taxItem.num_of_items;
        if (taxItem.price_before_tax) {
            this.price_before_tax = this.getPriceBeforeTax()
                .add(taxItem.getPriceBeforeTax())
                .toFormat('0.0000');
        }
        if (taxItem.additional_discount_amount) {
            this.additional_discount_amount = this.getAdditionalDiscountAmount()
                .add(taxItem.getAdditionalDiscountAmount())
                .toFormat('0.0000');
        }
        if (taxItem.additional_count_amount) {
            this.additional_count_amount = this.getAdditionalCountAmount()
                .add(taxItem.getAdditionalCountAmount())
                .toFormat('0.0000');
        }
        this.value = this.getValue()
            .add(taxItem.getValue())
            .toFormat('0.0000');
        ;
    }
    static merge(taxItems) {
        const taxes = [];
        taxItems.forEach(t => {
            const taxItem = t.$duplicate();
            const existingTaxItem = taxes.find(item => item.sameAs(taxItem));
            if (!existingTaxItem) {
                taxes.push(taxItem);
            }
            else {
                existingTaxItem.mergeWith(taxItem);
            }
        });
        return taxes;
    }
}
export default TaxItem;
modelRegistry.register('TaxItem', TaxItem);
//# sourceMappingURL=TaxItem.js.map